import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaWhatsapp, FaInstagram } from 'react-icons/fa';
import './NewsCard.css';
import { API_BASE } from '../api';
import GalleryViewer from './GalleryViewer';
import { getThumbnail, generateSrcSet } from '../utils/imageOptimization';

// Lightweight toast helper (DOM-based) to avoid adding a full toast library
function showToast(message, duration = 2500) {
  try {
    const id = 'site-toast';
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('div');
      el.id = id;
      el.style.position = 'fixed';
      el.style.bottom = '20px';
      el.style.left = '50%';
      el.style.transform = 'translateX(-50%)';
      el.style.zIndex = 9999;
      document.body.appendChild(el);
    }

    const msg = document.createElement('div');
    msg.textContent = message;
    msg.style.background = 'rgba(0,0,0,0.8)';
    msg.style.color = 'white';
    msg.style.padding = '10px 14px';
    msg.style.borderRadius = '6px';
    msg.style.marginTop = '8px';
    msg.style.boxShadow = '0 6px 18px rgba(0,0,0,0.18)';
    msg.style.fontSize = '14px';
    msg.style.maxWidth = '90vw';
    msg.style.textAlign = 'center';
    el.appendChild(msg);

    setTimeout(() => {
      msg.style.transition = 'opacity 0.25s ease, transform 0.25s ease';
      msg.style.opacity = '0';
      msg.style.transform = 'translateY(6px)';
    }, duration - 250);
    setTimeout(() => { msg.remove(); }, duration);
  } catch (e) {
    // ignore DOM errors
  }
}

function NewsCard({ news, featured = false }) {
  const getCategoryLabel = () => {
    const raw = String(news.category || '').trim();
    const lower = raw.toLowerCase();
    if (news.isGallery || lower.includes('gallery')) return 'RR NEWS TV GALLERY';
    if (news.isEvent || lower.includes('event')) return 'RR NEWS TV EVENT';
    if (news.isUjala || lower.includes('ujala') || lower.includes('moradabad')) return 'RR NEWS TV FASTEST UPDATE';
    return raw || 'RR NEWS TV FASTEST UPDATE';
  };

  const categoryLabel = getCategoryLabel();

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('hi-IN', options);
  };

    // Prefer a configured public frontend URL for shares (useful if building
    // on CI or your machine still runs localhost). Set `REACT_APP_FRONTEND_URL`
    // to your deployed frontend domain (e.g. https://moradabadujala.in)
    const configuredFrontend = process.env.REACT_APP_FRONTEND_URL || process.env.REACT_APP_PUBLIC_FRONTEND_URL;
    const frontendBase = (configuredFrontend && configuredFrontend.replace(/\/$/, '')) || (typeof window !== 'undefined' ? window.location.origin : '');
    const pageUrl = frontendBase ? `${frontendBase.replace(/\/$/, '')}/news/${news.slug || news._id}` : `/news/${news.slug || news._id}`;

  // Build a canonical share-preview URL that points to the backend share page
  // Accept REACT_APP_API_URL or API_BASE that may include or omit a trailing `/api`.
  const rawApi = (API_BASE || process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  let sharePreviewUrl = pageUrl;
  try {
    if (rawApi) {
      // If rawApi already includes '/api', keep it and append '/news/share/:slug'
      if (/\/api$/.test(rawApi)) {
        sharePreviewUrl = `${rawApi}/news/share/${encodeURIComponent(news.slug || news._id)}`;
      } else {
        // rawApi is likely the server root -> construct /api/news/share/:slug
        sharePreviewUrl = `${rawApi}/api/news/share/${encodeURIComponent(news.slug || news._id)}`;
      }
    }
  } catch (e) {
    sharePreviewUrl = pageUrl;
  }
  const shareTitle = news.title || news.heading || '';

  const shareWhatsApp = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();

    // Prefer the frontend page URL (readable slug) so shared messages show a friendly link.
    // Fall back to backend share-preview URL if frontend URL is not available.
    const link = pageUrl || sharePreviewUrl;
    // Sanitize title (remove stray markdown asterisks and normalize whitespace)
    const safeTitle = (shareTitle || '').toString().replace(/\*/g, '').replace(/\s+/g, ' ').trim();
    // Put the human-friendly title first, then the link — this yields a cleaner WhatsApp message
    const text = `RR NEWS TV FASTEST UPDATE — ${safeTitle}\n\n${link}`;
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener');
  };

  const shareInstagram = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    // For native share on mobile, prefer the frontend URL so the opened link
    // lands users inside the app/site frontend. For web fallback we copy the
    // frontend URL to clipboard.
    // Prefer frontend URL (friendly slug) when sharing; fallback to backend preview URL
    const link = pageUrl || sharePreviewUrl;
    const safeTitle = (shareTitle || '').toString().replace(/\*/g, '').replace(/\s+/g, ' ').trim();
    const shareData = { title: `RR NEWS TV FASTEST UPDATE - ${safeTitle}`, text: safeTitle, url: link };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // user cancelled or error
      }
      return;
    }

    // Fallback: copy link and open Instagram web so user can paste
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(pageUrl);
        // small UX: inform the user via toast
        showToast('Link copied to clipboard. Open Instagram and paste the link to share.');
      }
    } catch (err) {
      // ignore clipboard failures
    }
    window.open('https://www.instagram.com', '_blank');
  };


  return (
    <Link to={`/news/${news.slug}`} className={`news-card ${featured ? 'featured' : ''}`}>
      {/* marquee above the image, only for ujala items */}
      {news.isUjala && (
        <div className="ujala-marquee css-marquee" role="region" aria-label="RR शीर्षक">
          <div className="marquee-track">RR NEWS TV FASTEST UPDATE — खबर आपकी, नज़र हमारी</div>
        </div>
      )}

      <div className="news-image">
        {/* If a media URL is present, try to render video or audio; fallback to image on error */}
        {news.videoUrl ? (
          <MediaPreview src={news.videoUrl} alt={news.title} imgFallback={news.imageUrl} />
        ) : (
          // If this is a gallery post, show a compact viewer (single image with left/right controls)
          news.isGallery && Array.isArray(news.galleryImages) && news.galleryImages.length > 0 ? (
            <GalleryViewer images={news.galleryImages} alt={news.title} showThumbs={false} compact={true} />
          ) : (
            // fallback to single image with Cloudflare optimization
            (() => {
              const srcCandidate = news.imageUrl;
              const resolved = (srcCandidate && (srcCandidate.startsWith('http') || srcCandidate.startsWith('data:'))) ? srcCandidate : (API_BASE ? `${API_BASE}${srcCandidate && srcCandidate.startsWith('/') ? '' : '/'}${srcCandidate}` : srcCandidate);
              
              // Optimize image for faster loading
              const optimizedSrc = getThumbnail(resolved);
              const srcSet = generateSrcSet(resolved, [320, 640, 800]);
              
              return <img 
                src={optimizedSrc} 
                srcSet={srcSet}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
                alt={news.title}
                loading="lazy"
                width="400"
                height="225"
                decoding="async"
              />;
            })()
          )
        )}
        {news.isBreaking && <span className="breaking-badge">⚡ ब्रेकिंग न्यूज़</span>}
        {news.videoUrl && <span className="video-badge">▶️</span>}
      </div>
      <div className="news-content">
        <span className="news-category">{categoryLabel}</span>
        <h3 className="news-title">{news.title}</h3>
        {/* Show location for Ujala items */}
        {news.isUjala && news.location && (
          <div className="news-location">📍 {news.location}</div>
        )}
        <p className="news-description">{news.description}</p>
        <div className="news-meta">
          <span className="news-date">📅 {formatDate(news.createdAt)}</span>
          <span className="news-views">👁️ {news.views.toLocaleString('hi-IN')}</span>
        </div>
        {/* Share buttons for Ujala items (show for gallery/event/ujala category) */}
        {news.isUjala && (
          <div className="share-buttons" aria-label="Share this news">
            <button
              className="share-button whatsapp"
              onClick={shareWhatsApp}
              onMouseDown={(ev) => ev.stopPropagation()}
              onTouchStart={(ev) => ev.stopPropagation()}
              title="Share on WhatsApp"
              type="button"
              aria-label="Share on WhatsApp"
            >
              <FaWhatsapp size={18} aria-hidden />
            </button>

            <button
              className="share-button instagram"
              onClick={shareInstagram}
              onMouseDown={(ev) => ev.stopPropagation()}
              onTouchStart={(ev) => ev.stopPropagation()}
              title="Share on Instagram"
              type="button"
              aria-label="Share on Instagram"
            >
              <FaInstagram size={18} aria-hidden />
            </button>
          </div>
        )}
      </div>
    </Link>
  );
}

function MediaPreview({ src, alt, imgFallback }) {
  const [failed, setFailed] = useState(false);

  const makeSrc = (p) => {
    if (!p || p === 'null' || p === 'undefined') return '';
    if (/^https?:\/\//i.test(p) || p.startsWith('data:')) return p;
    const path = p.startsWith('/') ? p : '/' + p;
    return (API_BASE || '') + path;
  };

  const fallbackSrc = makeSrc(imgFallback) || '/placeholder.svg';

  if (failed) return <img src={fallbackSrc} alt={alt} />;

  // simple audio extension check
  const audioExt = /\.(mp3|wav|ogg|m4a|aac)$/i;
  const resolvedSrc = makeSrc(src);

  if (!resolvedSrc) return <img src={fallbackSrc} alt={alt} />;

  if (audioExt.test(resolvedSrc)) {
    return (
      <audio
        src={resolvedSrc}
        controls
        preload="metadata"
        onError={() => setFailed(true)}
        style={{ width: '100%' }}
      />
    );
  }

  return (
    <video
      src={resolvedSrc}
      controls
      playsInline
      preload="metadata"
      onError={() => setFailed(true)}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
}

export default NewsCard;


